/*
 * Lightweight pub/sub for cross-component UI intents.
 *
 * Some actions (e.g. "add section") live inside a deeply-nested page
 * component but are surfaced as shortcuts from the masthead's legend.
 * Rather than lift the action's state up to a shared context — which
 * would force every page through a sync layer — pages subscribe to a
 * named intent and the masthead emits it.
 *
 * Intents are one-shot: subscribers receive a ping, no payload is
 * carried. If an intent has no subscriber the emit is a no-op.
 */

import { useEffect } from 'react';

type Handler = () => void;

const subscribers = new Map<string, Set<Handler>>();

/** Emit an intent to whichever page (if any) is currently subscribed. */
export function emitIntent(intent: string): void {
  subscribers.get(intent)?.forEach((cb) => cb());
}

/** Subscribe a component to an intent; returns an unsubscribe fn. */
export function onIntent(intent: string, cb: Handler): () => void {
  let set = subscribers.get(intent);
  if (!set) {
    set = new Set();
    subscribers.set(intent, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
  };
}

/** React hook: subscribe for the lifetime of the component. */
export function useIntent(intent: string, cb: Handler): void {
  useEffect(() => onIntent(intent, cb), [intent, cb]);
}
